import React, { useState, useEffect } from 'react';
import { documentoAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    CheckCircle, 
    XCircle, 
    FileText, 
    Truck, 
    Building, 
    User, 
    Clock,
    Loader2,
    RefreshCw,
    AlertTriangle,
} from 'lucide-react';

export default function AprobarGuiasPage() {
    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [actionType, setActionType] = useState(null); // 'aprobar' | 'rechazar'
    const [observacion, setObservacion] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        loadDocumentos();
    }, []);

    const loadDocumentos = async () => {
        setLoading(true);
        try {
            const response = await documentoAPI.getAll('pendiente');
            setDocumentos(response.data);
        } catch (err) {
            console.error('Error loading documentos:', err);
            toast.error('Error al cargar las guías');
        } finally {
            setLoading(false);
        }
    };

    const openDialog = (doc, type) => {
        setSelectedDoc(doc);
        setActionType(type);
        setObservacion('');
    };

    const closeDialog = () => {
        setSelectedDoc(null);
        setActionType(null);
        setObservacion('');
    };

    const handleApproval = async () => {
        if (!selectedDoc) return;

        setProcessing(true);
        try {
            await documentoAPI.aprobar(selectedDoc.id, {
                documento_id: selectedDoc.id,
                estado: actionType === 'aprobar' ? 'autorizado' : 'rechazado',
                observacion: observacion || null,
            });

            toast.success(
                actionType === 'aprobar' 
                    ? 'Guía autorizada exitosamente' 
                    : 'Guía rechazada',
                {
                    description: `Folio: ${selectedDoc.folio}`,
                }
            );

            closeDialog();
            loadDocumentos();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al procesar la guía');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Aprobar Guías
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {documentos.length} guía{documentos.length !== 1 ? 's' : ''} pendiente{documentos.length !== 1 ? 's' : ''} de aprobación
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        onClick={loadDocumentos}
                        className="border-2"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                </div>

                {/* Alert if there are pending documents */}
                {documentos.length > 0 && (
                    <Card className="border-2 border-warning bg-warning/5">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <AlertTriangle className="h-5 w-5 text-warning" />
                                <p className="text-sm font-medium">
                                    Tiene {documentos.length} guía{documentos.length !== 1 ? 's' : ''} pendiente{documentos.length !== 1 ? 's' : ''} de revisión
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Documents Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} className="h-64" />
                        ))}
                    </div>
                ) : documentos.length === 0 ? (
                    <Card className="border-2">
                        <CardContent className="py-16">
                            <div className="empty-state">
                                <CheckCircle className="empty-state-icon text-success" />
                                <p className="empty-state-title">Sin guías pendientes</p>
                                <p className="empty-state-description">
                                    Todas las guías han sido procesadas
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {documentos.map((doc) => (
                            <Card 
                                key={doc.id} 
                                className="border-2 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] transition-shadow"
                                data-testid={`doc-card-${doc.folio}`}
                            >
                                <CardHeader className="card-industrial-header pb-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex flex-col">
                                            <span className="folio-display">{doc.folio}</span>
                                            {doc.folio_fisico && (
                                                <span className="text-xs font-mono text-muted-foreground">N° {doc.folio_fisico}</span>
                                            )}
                                        </div>
                                        <StatusBadge estado={doc.estado} size="sm" />
                                    </div>
                                </CardHeader>
                                <CardContent className="p-4 space-y-4">
                                    {/* Info Grid */}
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div className="flex items-center gap-2">
                                            <Truck className="h-4 w-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Patente</p>
                                                <p className="font-mono font-medium">{doc.patente_tracto || doc.patente}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Building className="h-4 w-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Destino</p>
                                                <p className="font-medium truncate">
                                                    {doc.razon_social || doc.destino_info?.razon_social || doc.rut_empresa || doc.rut_destino}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <User className="h-4 w-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Solicitante</p>
                                                <p className="font-medium">{doc.usuario_solicitante_nombre}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Clock className="h-4 w-4 text-muted-foreground" />
                                            <div>
                                                <p className="text-xs text-muted-foreground">Fecha</p>
                                                <p className="font-medium">{formatDate(doc.created_at)}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Items preview */}
                                    {doc.items && doc.items.length > 0 && (
                                        <div className="p-3 bg-muted/50 rounded-sm">
                                            <p className="text-xs text-muted-foreground mb-2">Items ({doc.items.length})</p>
                                            <ul className="text-sm space-y-1">
                                                {doc.items.slice(0, 3).map((item, idx) => (
                                                    <li key={idx} className="truncate">
                                                        • {item.descripcion} ({item.cantidad} {item.unidad})
                                                    </li>
                                                ))}
                                                {doc.items.length > 3 && (
                                                    <li className="text-muted-foreground">
                                                        + {doc.items.length - 3} más...
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Description */}
                                    {doc.descripcion && (
                                        <p className="text-sm text-muted-foreground line-clamp-2">
                                            {doc.descripcion}
                                        </p>
                                    )}

                                    {/* Actions */}
                                    <div className="flex gap-2 pt-2 flex-wrap">
                                        <Button
                                            onClick={() => window.location.assign(`/guias/documento/${doc.id}`)}
                                            variant="outline"
                                            className="border-2"
                                            data-testid={`ver-${doc.folio}`}
                                        >
                                            Ver detalle
                                        </Button>
                                        <Button
                                            onClick={() => openDialog(doc, 'aprobar')}
                                            className="flex-1 btn-industrial bg-success text-success-foreground"
                                            data-testid={`aprobar-${doc.folio}`}
                                        >
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                            Autorizar
                                        </Button>
                                        <Button
                                            onClick={() => openDialog(doc, 'rechazar')}
                                            variant="outline"
                                            className="flex-1 border-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                            data-testid={`rechazar-${doc.folio}`}
                                        >
                                            <XCircle className="h-4 w-4 mr-2" />
                                            Rechazar
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Approval Dialog */}
                <Dialog open={!!selectedDoc} onOpenChange={() => closeDialog()}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-heading uppercase">
                                {actionType === 'aprobar' ? 'Autorizar' : 'Rechazar'} Guía
                            </DialogTitle>
                            <DialogDescription>
                                {selectedDoc?.folio} - {selectedDoc?.patente}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label className="input-label">
                                    Observación {actionType === 'rechazar' && <span className="text-destructive">*</span>}
                                </Label>
                                <Textarea
                                    value={observacion}
                                    onChange={(e) => setObservacion(e.target.value)}
                                    placeholder={
                                        actionType === 'aprobar'
                                            ? 'Observación opcional...'
                                            : 'Motivo del rechazo (requerido)...'
                                    }
                                    className="min-h-[100px] border-2"
                                    data-testid="observacion-input"
                                />
                            </div>

                            {actionType === 'aprobar' && (
                                <div className="p-3 bg-success/10 border border-success/30 rounded-sm">
                                    <p className="text-sm text-success font-medium">
                                        Al autorizar, la guía pasará al área de seguridad para su despacho.
                                    </p>
                                </div>
                            )}

                            {actionType === 'rechazar' && (
                                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-sm">
                                    <p className="text-sm text-destructive font-medium">
                                        El solicitante será notificado del rechazo con la observación indicada.
                                    </p>
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={closeDialog}
                                className="border-2"
                                data-testid="cancel-dialog-btn"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleApproval}
                                disabled={processing || (actionType === 'rechazar' && !observacion.trim())}
                                className={`btn-industrial ${
                                    actionType === 'aprobar'
                                        ? 'bg-success text-success-foreground'
                                        : 'bg-destructive text-destructive-foreground'
                                }`}
                                data-testid="confirm-action-btn"
                            >
                                {processing ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : actionType === 'aprobar' ? (
                                    <>
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        Autorizar
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="h-4 w-4 mr-2" />
                                        Rechazar
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
